import React, { useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import worldMapData from '../data/world-countries.json';

const WorldMap = ({ 
  data = [], 
  selectedMetric = 'Partner Risk',
  onCountryClick = () => {},
  height = 500,
  className = "",
  enableZoom = false
}) => {
  const mapRef = useRef();
  const svgRef = useRef();

  // Color scale based on selected metric
  const getColorScale = useCallback((countriesData) => {
    const metricMap = {
      'Import Ratio': 'dependency_ratio',
      'World Reserves': 'world_reserves', 
      'Domestic Reserves': 'domestic_reserves',
      'Trade Restrictions': 'trade_restrictions',
      'Partner Risk': 'partner_risk'
    };
    
    const metricKey = metricMap[selectedMetric] || 'partner_risk';
    
    // For Import Ratio, use all values including 0 for proper scale calculation
    let values;
    if (selectedMetric === 'Import Ratio') {
      values = countriesData
        .map(d => d[metricKey])
        .filter(v => v !== null && v !== undefined && v !== 'N/A');
    } else {
      values = countriesData.map(d => d[metricKey] || 0);
    }
    
    const maxValue = values.length > 0 ? Math.max(...values) : 1;
    
    // Use custom color scheme for different metrics
    let colorScale;
    if (selectedMetric === 'Import Ratio') {
      // Use fixed percentage domain to match the legend (0-100%)
      // Adjusted to make low values (1%+) more visible
      colorScale = d3.scaleLinear()
        .domain([0, 1, 10, 25, 50, 100])
        .range([
          '#ffffff',      // White for 0% (NA/no data)
          '#f0f9e8',      // Very light green for 1%
          '#a6d4da',      // Light blue-green for 10%
          '#4ba8c9',      // Blue for 25%
          '#387aab',      // Dark blue for 50%
          '#254b8c'       // Darkest blue for 100%
        ]);
    } else if (selectedMetric === 'Trade Restrictions') {
      colorScale = d3.scaleLinear()
        .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
        .range([
          '#e5e7eb',      // Light gray for no restrictions
          '#fef3c7',      // Light yellow for low restrictions
          '#fbbf24',      // Yellow for medium restrictions
          '#f59e0b',      // Orange for high restrictions
          '#dc2626'       // Red for very high restrictions
        ]);
    } else if (selectedMetric === 'Partner Risk') {
      // Partner Risk color scheme (fixed range for consistency across minerals)
      colorScale = d3.scaleLinear()
        .domain([0, 1, 2, 3, 4, 5])
        .range([
          '#ffffff',      // Red for very high risk (0-1)
          '#fb8171',      // Light red for high risk (1-2)
          '#e9e0c8',      // Beige for medium-high risk (2-3)
          '#7cb5c0',      // Light blue for medium risk (3-4)
          '#00768d'       // Dark blue for low risk (4-5)
        ]);
    } else {
      // Default color scheme for other metrics
      colorScale = d3.scaleLinear()
        .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
        .range([
          '#e5e7eb',      // Light gray for no data
          '#86efac',      // Light green
          '#fde047',      // Light yellow
          '#60a5fa',      // Light blue
          '#fbbf24'       // Amber/yellow
        ]);
    }
    
    return colorScale;
  }, [selectedMetric]);

  // Render map
  const renderMap = useCallback(() => {
    console.log('🗺️ WorldMap renderMap called with:', {
      hasMapRef: !!mapRef.current,
      hasSvgRef: !!svgRef.current,
      dataLength: data?.length || 0,
      selectedMetric,
      dataSample: data?.slice(0, 2)
    });
    if (!mapRef.current || !svgRef.current) {
      return;
    }

    try {
      const width = mapRef.current.clientWidth || 800;
      const height = mapRef.current.clientHeight || 600;
      
      // Clear previous map and tooltips
      d3.select(svgRef.current).selectAll("*").remove();
      d3.selectAll('.tooltip').remove();
      
      // Remove any existing drag and zoom listeners
      d3.select(svgRef.current).on('.drag', null);
      d3.select(svgRef.current).on('.zoom', null);

      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background-color', '#ffffff'); // White background

      // Create tooltip
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '8px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');

      // Create projection
      const initialScale = 150;
      const initialTranslate = [width / 2, height / 2];
      
      const projection = d3.geoNaturalEarth1()
        .scale(initialScale)
        .translate(initialTranslate);

      let path = d3.geoPath().projection(projection);

      // Create zoom group if zoom is enabled
      let zoomGroup = null;
      if (enableZoom) {
        zoomGroup = svg.append('g').attr('class', 'zoom-group');
      }

      // Setup zoom behavior if enabled
      let zoom = null;
      if (enableZoom) {
        zoom = d3.zoom()
          .scaleExtent([1, 8]) // Allow zooming from 1x to 8x
          .on('zoom', (event) => {
            // Apply zoom transform to the projection
            const { transform } = event;
            
            // For geographic projections, we update the scale and translate
            // Calculate new scale based on zoom level
            const newScale = initialScale * transform.k;
            
            // Calculate new translate based on zoom transform
            // Adjust translate to account for the zoom transform offset
            // The transform.x and transform.y are in SVG pixel coordinates
            const newTranslateX = initialTranslate[0] - transform.x / transform.k;
            const newTranslateY = initialTranslate[1] - transform.y / transform.k;
            
            projection
              .scale(newScale)
              .translate([newTranslateX, newTranslateY]);
            
            // Update path with new projection
            path = d3.geoPath().projection(projection);
            
            // Update all country paths
            if (zoomGroup) {
              zoomGroup.selectAll('.country')
                .attr('d', path);
            }
          });
        
        // Apply zoom to SVG
        svg.call(zoom);
      }

      // Add smooth transition for initial render
      svg.style('opacity', 0)
        .transition()
        .duration(300)
        .style('opacity', 1);

      // Transform data to match expected format
      let countriesData = [];
      if (data && Array.isArray(data)) {
        countriesData = data.map(country => ({
          country: country.name || country.country,
          dependency_ratio: country.dependencyRatio || country.dependency_ratio || 0,
          world_reserves: country.worldReserves || country.world_reserves || 0,
          domestic_reserves: country.domesticReserves || country.domestic_reserves || 0,
          trade_restrictions: country.tradeRestrictions || country.trade_restrictions || 0,
          partner_risk: country.partnerRisk || country.partner_risk || 0,
          wgi_score: country.wgi_score || 0,
          fsi_score: country.fsi_score || 0,
          risk_level: country.risk_level || 'Unknown',
          import_percentage: country.import_percentage || 0,
          trade_volume: country.trade_volume || 0,
          iso3: country.iso3,
          name: country.name || country.country,
          color: country.color || '#059669'
        }));
      }

      const colorScale = getColorScale(countriesData);
      
      // Debug: Log color scale info for Partner Risk
      if (selectedMetric === 'Partner Risk') {
        console.log('🎨 Partner Risk Color Scale Debug:', {
          domain: colorScale.domain(),
          range: colorScale.range(),
          dataLength: countriesData.length,
          sampleValues: countriesData.slice(0, 3).map(d => d.partner_risk)
        });
      }

      // Draw countries - use zoomGroup if zoom is enabled, otherwise use svg directly
      const container = enableZoom ? zoomGroup : svg;
      
      const countries = container.selectAll('.country')
        .data(worldMapData.features)
        .enter()
        .append('path')
        .attr('class', 'country')
        .attr('d', path)
        .attr('fill', d => {
          const countryName = d.properties.name || d.properties.NAME;
          
          const countryData = countriesData.find(c => {
            if (!c || !countryName) return false;
            
            const countryField = c.country || c.name || c.country_name || c.countryName;
            if (!countryField) return false;
            
            // Try exact match first
            if (countryField.toLowerCase() === countryName.toLowerCase()) {
              return true;
            }
            
            // Try alternative names
            const altNames = c.altNames || [];
            return altNames.some(alt => 
              alt.toLowerCase() === countryName.toLowerCase()
            );
          });
          
          // For World Reserves, always return white regardless of data
          if (selectedMetric === 'World Reserves') {
            return '#ffffff';
          }
          
          if (countryData) {
            const metricKey = {
              'Import Ratio': 'dependency_ratio',
              'World Reserves': 'world_reserves', 
              'Domestic Reserves': 'domestic_reserves',
              'Trade Restrictions': 'trade_restrictions',
              'Partner Risk': 'partner_risk'
            }[selectedMetric] || 'partner_risk';
            
            const value = countryData[metricKey];
            
            // For Import Ratio, return white for NA/null/undefined or 0 values
            if (selectedMetric === 'Import Ratio') {
              if (value === null || value === undefined || value === 'N/A' || value === 0) {
                return '#ffffff';
              }
            }
            
            const actualValue = value || 0;
            return colorScale(actualValue);
          }
          
          // For countries without data, return white for Import Ratio, otherwise use color scale with 0 value
          if (selectedMetric === 'Import Ratio') {
            return '#ffffff';
          }
          return colorScale(0);
        })
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 0.5)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          const countryName = d.properties.name || d.properties.NAME;
          const countryData = countriesData.find(c => {
            const countryField = c.country || c.name || c.country_name || c.countryName;
            if (!countryField) return false;
            return countryField.toLowerCase() === countryName.toLowerCase();
          });
          
          if (countryData) {
            const metricKey = {
              'Import Ratio': 'dependency_ratio',
              'World Reserves': 'world_reserves', 
              'Domestic Reserves': 'domestic_reserves',
              'Trade Restrictions': 'trade_restrictions',
              'Partner Risk': 'partner_risk'
            }[selectedMetric] || 'partner_risk';
            
            const value = countryData[metricKey] || 0;
            
            tooltip.transition()
              .duration(200)
              .style('opacity', .9);
            
            if (selectedMetric === 'Partner Risk') {
              // Enhanced tooltip for Partner Risk
              const riskLevel = countryData.risk_level || 'Unknown';
              const dependencyRatio = countryData.dependency_ratio || 0;
              const wgiScore = countryData.wgi_score || 0;
              const fsiScore = countryData.fsi_score || 0;
              const importPercentage = countryData.import_percentage || 0;
              
              tooltip.html(`
                <div class="font-semibold text-white">${countryName}</div>
                <div class="text-blue-300">${value.toFixed(2)}</div>
                <div class="text-gray-300 text-sm">Risk Level: ${riskLevel}</div>
                <div class="text-gray-300 text-sm">Dependency: ${(dependencyRatio * 100).toFixed(1)}%</div>
                <div class="text-gray-300 text-sm">WGI Score: ${wgiScore.toFixed(2)}</div>
                <div class="text-gray-300 text-sm">FSI Score: ${fsiScore.toFixed(2)}</div>
                ${importPercentage > 0 ? `<div class="text-gray-300 text-sm">Import Share: ${importPercentage.toFixed(1)}%</div>` : ''}
              `)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 10) + 'px');
            } else {
              // Standard tooltip for other metrics
              tooltip.html(`
                <div class="font-semibold">${countryName}</div>
                <div>${selectedMetric}: ${value.toFixed(2)}</div>
              `)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 10) + 'px');
            }
          }
        })
        .on('mouseout', function() {
          tooltip.transition()
            .duration(500)
            .style('opacity', 0);
        })
        .on('click', function(event, d) {
          const countryName = d.properties.name || d.properties.NAME;
          const countryData = countriesData.find(c => {
            const countryField = c.country || c.name || c.country_name || c.countryName;
            if (!countryField) return false;
            return countryField.toLowerCase() === countryName.toLowerCase();
          });
          
          if (countryData) {
            onCountryClick(countryData, event);
          }
        });

      // Add zoom controls (only when zoom is enabled)
      if (enableZoom && zoom) {
        const zoomControls = svg.append('g')
          .attr('class', 'zoom-controls')
          .attr('transform', `translate(20, 20)`);
        
        // Zoom in button
        const zoomIn = zoomControls.append('g')
          .attr('class', 'zoom-control zoom-in')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 0)')
          .on('click', () => {
            svg.transition().call(
              zoom.scaleBy, 1.5
            );
          });
        
        zoomIn.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        zoomIn.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '18px')
          .attr('fill', '#4b5563')
          .text('+');
        
        // Zoom out button
        const zoomOut = zoomControls.append('g')
          .attr('class', 'zoom-control zoom-out')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 35)')
          .on('click', () => {
            svg.transition().call(
              zoom.scaleBy, 1 / 1.5
            );
          });
        
        zoomOut.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        zoomOut.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '18px')
          .attr('fill', '#4b5563')
          .text('−');
        
        // Reset zoom button
        const resetZoom = zoomControls.append('g')
          .attr('class', 'zoom-control reset-zoom')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 70)')
          .on('click', () => {
            // Reset zoom transform - the zoom event handler will automatically update the projection
            svg.transition().duration(750).call(
              zoom.transform,
              d3.zoomIdentity
            );
          });
        
        resetZoom.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        resetZoom.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '14px')
          .attr('fill', '#4b5563')
          .text('⌂');
        
        // Hover effects
        zoomControls.selectAll('.zoom-control')
          .on('mouseover', function() {
            d3.select(this).select('rect')
              .attr('fill', '#f3f4f6')
              .attr('stroke', '#2563eb');
            d3.select(this).select('text')
              .attr('fill', '#2563eb');
          })
          .on('mouseout', function() {
            d3.select(this).select('rect')
              .attr('fill', 'white')
              .attr('stroke', '#4b5563');
            d3.select(this).select('text')
              .attr('fill', '#4b5563');
          });
      }

      // Add legend
      const legendWidth = 200;
      const legendHeight = 20;
      const legendX = width - legendWidth - 20;
      const legendY = height - legendHeight - 20;
      
      const legend = svg.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${legendX}, ${legendY})`);
      
      // Legend title
      legend.append('text')
        .attr('x', 0)
        .attr('y', -5)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', '#474747')
        .text(selectedMetric);
      
      // Legend scale - use fixed domain for Partner Risk
      const legendScale = d3.scaleLinear()
        .domain([0, 5])
        .range([0, legendWidth]);
      
      // Debug: Log legend scale info
      console.log('📊 Legend Scale Debug:', {
        domain: legendScale.domain(),
        range: legendScale.range(),
        legendWidth: legendWidth
      });
      
      // Legend axis - explicitly set tick values for Partner Risk
      const legendAxis = d3.axisBottom(legendScale)
        .tickValues([0, 1, 2, 3, 4, 5])
        .tickFormat(d3.format('.0f'));
      
      legend.append('g')
        .attr('class', 'legend-axis')
        .attr('transform', `translate(0, ${legendHeight})`)
        .style('color', '#474747')
        .call(legendAxis);
      
      // Legend gradient
      const defs = svg.append('defs');
      const gradient = defs.append('linearGradient')
        .attr('id', 'legend-gradient')
        .attr('x1', '0%')
        .attr('x2', '100%')
        .attr('y1', '0%')
        .attr('y2', '0%');
      
      // Add gradient stops - use fixed domain for Partner Risk
      const range = colorScale.range();
      const numStops = range.length;
      
      range.forEach((color, i) => {
        gradient.append('stop')
          .attr('offset', `${(i / (numStops - 1)) * 100}%`)
          .attr('stop-color', color);
      });
      
      // Legend rectangle
      legend.append('rect')
        .attr('width', legendWidth)
        .attr('height', legendHeight)
        .style('fill', 'url(#legend-gradient)')
        .style('stroke', '#474747')
        .style('stroke-width', 1);

    } catch (error) {
      console.error('Error rendering map:', error);
    }
  }, [data, selectedMetric, getColorScale, onCountryClick, enableZoom]);

  useEffect(() => {
    console.log('🔄 WorldMap useEffect triggered:', {
      hasMapRef: !!mapRef.current,
      hasSvgRef: !!svgRef.current,
      dataLength: data?.length || 0
    });
    if (mapRef.current && svgRef.current) {
      renderMap();
    }
  }, [renderMap, data]);

  return (
    <div 
      ref={mapRef} 
      className={`w-full border border-gray-300 rounded-lg bg-white relative ${className}`}
      style={{ minHeight: `${height}px` }}
    >
      <svg ref={svgRef} className="w-full h-full"></svg>
      {(!data || data.length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
          <div className="text-center">
            <div className="text-4xl mb-4">🗺️</div>
            <div className="text-gray-700 text-lg">No Partner Risk Data Available</div>
            <div className="text-gray-500 text-sm mt-2">Please select a different mineral or year</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMap;
